import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, timer} from 'rxjs';
import {environment} from '../../environments/environment';
import {AllDeviceListResponse} from './models/all-device-data';
import {GlobalConstants} from './global-constants';
import {UserSubRegionSelectionService} from '../header/user-sub-region-selection/user-sub-region-selection.service';
import {TokenService} from '../core/token.service';
import {tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DataPollingService {
  deviceListPollingInterval = environment.deviceListPollingInterval;
  connectivityKPIPollingInterval = environment.connectivityKPIPollingInterval;
  watchlistPollingInterval = environment.watchlistPollingInterval;
  allDeviceListBSubject = new BehaviorSubject<AllDeviceListResponse>(null);
  connectivityKPIBSubject = new BehaviorSubject<any>({
    online: 0,
    offline: 0,
    lost: 0,
    provisioned: 0,
  });
  watchListBSubject = new BehaviorSubject<any>(null);
  dashboardKPIBSubject = new BehaviorSubject<any>(null);

  constructor(private readonly httpClient: HttpClient,
              private readonly userSubRegionSelectionService: UserSubRegionSelectionService,
              private readonly tokenService: TokenService) {
    this.startOnTokenLoaded();
    this.userSubRegionSelectionService.userSubRegionSelection$
      .pipe(
        tap(() => this.getAllDevices()),
        tap(() => this.reloadConnectivityKPI()),
        tap(() => this.reloadDashboardKPIs()),
      )
      .subscribe();
  }

  private readonly startOnTokenLoaded = () => {
    this.tokenService.waitUntilLoaded$
      .pipe(tap((loaded) => this.startDataPolling(loaded)))
      .subscribe();
  }

  startDataPolling(loaded: boolean): any {
    this.startDeviceListPolling();
    this.startConnectivityKPIPolling();
    this.startWatchlistPolling();
    this.getDashboardKPIsPolling();
  }

  startDeviceListPolling(): void {
    timer(0, this.deviceListPollingInterval).subscribe((t) => this.getAllDevices());
  }


  private getAllDevices = () => {
    this.getAllDevicesFromAPI([], [], [], [], []).subscribe(
      (allDeviceListRes: AllDeviceListResponse) => {
        this.allDeviceListBSubject.next(allDeviceListRes);
      },
      (error) => {
        if (error.status === 401) {
          window.open(environment.redirectUrl, '_self');
        }
      });
  }

  // get all devices
  getAllDevicesFromAPI(
      selectedStatus,
      selectedModel,
      selectedServiceRegion,
      selectedProductGroup,
      selectedConnectionStatus,


  ): Observable<AllDeviceListResponse> {

    const body = {
      deviceStatus: selectedStatus ? selectedStatus : [],
      physicalDeviceModel: selectedModel ? selectedModel : [],
      serviceRegion: selectedServiceRegion ? selectedServiceRegion : [],
      productGroup: selectedProductGroup ? selectedProductGroup : [],
      customer: [],
      connectivityStatus: selectedConnectionStatus
          ? selectedConnectionStatus
          : [],
      allRecord: true,
      assignedOUList: localStorage.getItem(GlobalConstants.ASSIGNED_OU_LIST),
      pageNo: 1,
      pageSize : 999999,
    };
    const url = environment.baseUrl + environment.getAllDevices;
    return this.httpClient.post<AllDeviceListResponse>(url, body);
  }

  getAllDeviceList(): Observable<AllDeviceListResponse> {
    return this.allDeviceListBSubject.asObservable();
  }

  startConnectivityKPIPolling(): void {
    timer(0, this.connectivityKPIPollingInterval).subscribe(() => {
      if (!localStorage.getItem(GlobalConstants.ASSIGNED_OU_LIST)) {
        return;
      }
      this.getConnectivityKPIFromAPI().subscribe((connectivityKPI) => {
        this.connectivityKPIBSubject.next(connectivityKPI);
      });
    });


  }

  getConnectivityKPIFromAPI(): Observable<any[]> {
    const url = environment.baseUrl + environment.getConnectivityKPIs;
    return this.httpClient.get<any[]>(url);
  }

  getConnectivityKPI(): Observable<any[]> {
    return this.connectivityKPIBSubject.asObservable();
  }

  updateLastViewedForDevice(deviceId, timeStamp): void {
    const deviceListRespObj = this.allDeviceListBSubject.value;
    deviceListRespObj.result.forEach((device) => {
      if (device.deviceId === deviceId) {
        device.lastViewed = timeStamp;
      }
    });
    this.allDeviceListBSubject.next(deviceListRespObj);
  }

  startWatchlistPolling(): void {
    timer(0, this.watchlistPollingInterval).subscribe(() => {
      this.getWatchListKPIsFromAPI().subscribe((watchList) => {
        this.watchListBSubject.next(watchList);
      });
    });
  }
  // get watchlist KPIs count
  getWatchListKPIsFromAPI(): Observable<any[]> {
    const url = environment.baseUrl + environment.getWatchlist;
    return this.httpClient.get<any[]>(url, { withCredentials: false });
  }

  getDashboardKPIsPolling(): void {
    timer(0, this.watchlistPollingInterval).subscribe(() => {
      if (!localStorage.getItem(GlobalConstants.ASSIGNED_OU_LIST)) {
        return;
      }
      this.getDashboardKPIs().subscribe((dashboardKPI) => {
        this.dashboardKPIBSubject.next(dashboardKPI);
      });
    });
  }


  private reloadConnectivityKPI(): void {
    if (!localStorage.getItem(GlobalConstants.ASSIGNED_OU_LIST)) {
      return;
    }
    this.getConnectivityKPIFromAPI().subscribe((connectivityKPI) => {
      this.connectivityKPIBSubject.next(connectivityKPI);
    });
  }

  private reloadDashboardKPIs(): void {
    if (!localStorage.getItem(GlobalConstants.ASSIGNED_OU_LIST)) {
      return;
    }
    this.getDashboardKPIs().subscribe((dashboardKPI) => {
      this.dashboardKPIBSubject.next(dashboardKPI);
    });
  }


  getDashboardKPIs(): Observable<any[]> {
    const url = environment.baseUrl + environment.getDashboardKPIs;
    return this.httpClient.get<any[]>(url, { withCredentials: false });
  }

  getDashboardKPICount(): Observable<any>{
    return this.dashboardKPIBSubject.asObservable();
  }

  getWatchListKPIs(): Observable<any[]> {
    return this.watchListBSubject.asObservable();
  }



}
